import React, {
  createContext,
  FC,
  useCallback,
  useEffect,
  useMemo,
  useState,
  useRef,
  useContext,
  MutableRefObject,
} from 'react';
import { jwtDecode } from 'jwt-decode';
import { useErrorBoundary } from 'src/shared/components/ErrorBoundary';
import { appInsights } from 'src/shared/lib/appInsights';
import { useAmplitude } from 'src/lib/amplitude';
import { calcTimeToLive, setRefreshTimer } from 'src/api/apiUtils';
import useWhenMounted from 'src/lib/hooks/useWhenMounted';
import { useBackend } from 'src/components/provider/BackendProvider';
import { useAppState } from 'src/components/provider/AppStateProvider';
import { useAppointmentState } from 'src/components/provider/AppointmentProvider';

export interface SessionState {
  jwt: string;
  hasLeftSession: MutableRefObject<boolean>;
  telehealthVisitUserId: string;
  credentialsRefreshStopped: boolean;
  stopCredentialsRefresh: () => void;
  disableBeforeUnload: () => void;
  thVendor: 'chime' | 'zoom';
}

export const SessionContext = createContext<SessionState | null>(null);

/**
 * The SessionProvider is used to provide context for the session. It should wrap
 * the highest level of the application that requires session state. It takes
 * a token and display name to make the join-session call, and adds the jwt and
 * hasLeftSession state to the SessionContext.
 * @param Props session props
 */
const SessionProvider: FC<WithChildren> = ({ children }: WithChildren) => {
  const { attendeeName } = useAppState();
  const { joinToken } = useAppointmentState();
  const { baseUrl, joinSessionApi, refreshJWTApi } = useBackend();
  const { throwError } = useErrorBoundary();
  const [session, setSession] = useState<{
    jwt: string;
    telehealthVisitUserId: string;
    thVendor: 'chime' | 'zoom';
  }>({
    jwt: process.env.JWT || '',
    telehealthVisitUserId: '',
    thVendor: 'chime',
  });
  const [refreshRetryCount, setRefreshRetryCount] = useState(0); // easy way to cause the useEffect to run again and set a new timer, in the case that the refreshAPI failed.
  const [jwtRefreshStopped, setJwtRefreshStopped] = useState(false); // will be used in TelehealthMeetingProvider to determine if refresh is necessary
  const hasLeftSession = useRef<boolean>(false);
  const { isMounted } = useWhenMounted();
  const timerId = useRef<NodeJS.Timeout | null>();
  const pendingJwtCall = useRef<boolean>(false);
  const { ampTrackEvent } = useAmplitude();

  const { jwt, telehealthVisitUserId, thVendor } = session;

  // Any time jwt is updated, save it as a ref for use in the leaveOnUnload function
  const refJwt = useRef(jwt);
  useEffect(() => {
    refJwt.current = jwt;
  }, [jwt]);

  const leaveSession = useCallback(() => {
    if (navigator && navigator.sendBeacon && !hasLeftSession.current) {
      const blob = new Blob(['jwt=' + refJwt.current], { type: 'application/x-www-form-urlencoded' });
      navigator.sendBeacon(`${baseUrl}/leave-session?onunload=1`, blob);
    }
  }, [baseUrl]);

  useEffect(() => {
    if (joinToken && attendeeName && !jwt) {
      // First load, make request & setup callbacks
      joinSessionApi(joinToken, attendeeName, true)
        .then(({ jwt, telehealthVisitUserId, thVendor }) => {
          // In the rare event that a user lands on the Access Denied Page and then clicks the Retry
          // button right away, it will redirect them to the Welcome Page which unmounts the SessionProvider
          // as a result.  Under that scenario, this check will prevent setSession from being called on this
          // unmounted component.
          if (isMounted.current) {
            setSession({ jwt, telehealthVisitUserId, thVendor });
            window.addEventListener('beforeunload', leaveSession);
          }
        })
        .catch((e) => {
          throwError(e);
        });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [joinToken, attendeeName, leaveSession]);

  const disableBeforeUnload = useCallback(() => {
    window.removeEventListener('beforeunload', leaveSession);
  }, [leaveSession]);

  const handleJWTRefresh = () => {
    if (!pendingJwtCall.current) {
      pendingJwtCall.current = true;
      refreshJWTApi(jwt)
        .then((response) => {
          if (response) {
            setSession({ jwt: response.jwt, telehealthVisitUserId, thVendor });
          }
          pendingJwtCall.current = false;
        })
        .catch(() => {
          //We have a useEffect depends on refreshRetryCount so that in the case of api failure we'll run again and set a new timer.
          setRefreshRetryCount(refreshRetryCount + 1);
          pendingJwtCall.current = false;
        });
    }
  };

  const stopJwtRefresh = () => {
    clearTimeout(timerId.current as NodeJS.Timeout);
    timerId.current = null;
    if (isMounted.current) {
      setJwtRefreshStopped(true);
    }
  };

  const visibilityChangeHandler = useCallback(
    () => {
      if (document.hidden) {
        appInsights.trackEvent({ name: 'visibilityBackground' }, {});
        ampTrackEvent({ action: 'AppSentToBackground', pageNameInProps: true });

        // Once the document has been sent to the background, we can no longer trust the original timeout, especially on Chrome
        if (timerId.current) {
          clearTimeout(timerId.current as NodeJS.Timeout);
          timerId.current = null;
        }
      } else if (!document.hidden) {
        appInsights.trackEvent({ name: 'visibilityForeground' }, {});
        ampTrackEvent({ action: 'AppReturnedToForeground', pageNameInProps: true });

        // If the document just regained visibility, we need to re-evaluate our jwt situation.
        if (!timerId.current) {
          const TenMinutes = 60 * 1000 * 10;
          const decodedJWT: any = jwtDecode(jwt);
          const ttl = calcTimeToLive(decodedJWT.exp);
          // TODO what about if ttl < 0 ?
          if (ttl < TenMinutes) {
            // Get a new jwt if we normally would have tried by now. Try to get it now, no delay
            handleJWTRefresh();
          } else {
            // If we wouldn't have normally tried to refresh yet, let the normal logic do its thing and kick off a new timeout
            timerId.current = setRefreshTimer(decodedJWT.exp, handleJWTRefresh);
          }
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [jwt]
  );

  useEffect(
    () => {
      //TODO once the JavaBackEnd has been fully rolled out (GA), this url-matching logic should be removed and the refresh API should *always* be used.
      if (jwt && !hasLeftSession.current && !jwtRefreshStopped) {
        const decodedJWT: any = jwtDecode(jwt);
        timerId.current = setRefreshTimer(decodedJWT.exp, handleJWTRefresh);
        document.addEventListener('visibilitychange', visibilityChangeHandler);

        return () => {
          clearTimeout(timerId.current as NodeJS.Timeout);
          document.removeEventListener('visibilitychange', visibilityChangeHandler);
        };
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [jwt, refreshRetryCount, telehealthVisitUserId, hasLeftSession, thVendor, jwtRefreshStopped]
  );

  const value = useMemo(
    () => ({
      jwt,
      telehealthVisitUserId,
      thVendor,
      hasLeftSession,
      stopCredentialsRefresh: stopJwtRefresh,
      credentialsRefreshStopped: jwtRefreshStopped,
      disableBeforeUnload,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [jwt, telehealthVisitUserId, thVendor, hasLeftSession]
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
};

const noopState: SessionState = {
  jwt: '',
  hasLeftSession: { current: false },
  telehealthVisitUserId: '',
  credentialsRefreshStopped: false,
  stopCredentialsRefresh: () => null,
  disableBeforeUnload: () => null,
  thVendor: 'chime',
};

function useSessionState(): SessionState {
  const state = useContext(SessionContext) || noopState;
  return state;
}

export { useSessionState };

export default SessionProvider;
